import DesktopCarousel from '../Components/Home/DesktopCarousel';
import MobileCards from '../Components/Home/MobileCards';
import { Row, Col, Container } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import { useState, useEffect, useMemo } from "react";
import MakeRequest from '../Components/Helpers/MakeRequest';
import { useOutletContext, useNavigate } from 'react-router-dom';
import QuillExports from '.././Components/Helpers/QuillHelpers';

const Home = () => {
  const { l } = useOutletContext();
  const { SafeHtml } = QuillExports();
  const navigate = useNavigate();
  const [carouselItems, setCarouselItems] = useState([]);
  const [subtitles, setSubtitles] = useState([]);
  const [references, setReferences] = useState([]);
  const [products, setProducts] = useState([]);

  // Grid settings: lg=2 columns => 4 rows per batch = 8 items
  const BATCH = 8;
  const [visibleCount, setVisibleCount] = useState(BATCH);

  // shuffle util (Fisher–Yates)
  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  useEffect(() => {
    const fetchMenuItems = async () => {
      const promises = [
        MakeRequest({}, 'content/pageContent?page=home', 'get'),
        MakeRequest(null, `content/references`, 'get'),
        MakeRequest(null, `content/products`, 'get')
      ];

      const [homeContentRes, referenceRes, productRes] = await Promise.all(promises);

      if (homeContentRes.data) {
        const carouselContent = homeContentRes.data.filter(obj => obj.type === "carousel");
        setCarouselItems(carouselContent);

        const subtitleContent = homeContentRes.data.filter(obj => obj.type === "subtitle");
        setSubtitles(subtitleContent);
      }

      if (referenceRes.data) setReferences(referenceRes.data);
      if (productRes.data) setProducts(productRes.data.filter(obj => obj.type !== "productTypes"));
    };
    fetchMenuItems();
  }, []);

  // build a unified, randomized list with a type tag
  const mixedItems = useMemo(() => {
    const taggedProducts = products.map(p => ({ _kind: 'product', ...p }));
    const taggedRefs = references.map(r => ({ _kind: 'reference', ...r }));
    return shuffle([...taggedProducts, ...taggedRefs]);
  }, [products, references]);

  // Reset pagination when the set changes
  useEffect(() => {
    setVisibleCount(BATCH);
  }, [mixedItems.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // helper to detect image-like files in reference.fileContent
  const pickFirstImageFromRef = (ref) => {
    const img = ref?.fileContent?.find(f =>
      typeof f?.file === 'string' &&
      /\.(png|jpe?g|gif|webp|svg)$/i.test(f.file)
    );
    return img?.file || null;
  };

  const handleReadMore = (item) => {
    if (item._kind === 'product') {
      navigate(`/products/${item.name}`, { state: { product: item } });
    } else if (item._kind === 'reference') {
      navigate(`/references/${item.titleEng}`, { state: { reference: item } });
    }
  };

  const truncate100 = (t) => (t?.length > 100 ? t.slice(0, 100) + '...' : t);

  const canShowMore = visibleCount < mixedItems.length;

  return (
    <>
      <div className="d-none d-md-block">
        <Container className="mt-3">
          <DesktopCarousel content={carouselItems} />
        </Container>

        <Container className="mt-3">
          {subtitles.map((sub, idx) => {
            const hasImg = Boolean(sub?.imgSrc?.file);
            const flip = idx % 2 === 1 && hasImg;

            return (
              <Container key={idx} className="my-5">
                <Row
                  className={flip ? "flex-md-row-reverse" : ""}
                  xs={1}
                  md={hasImg ? 2 : 1}
                >
                  <Col md={hasImg ? 6 : 12} className="d-flex align-items-center">
                    <div className="w-100">
                      <SafeHtml html={l(sub.content.contentSwe, sub.content.contentEng)} />
                    </div>
                  </Col>

                  {hasImg && (
                    <Col md={6} className="d-flex align-items-center">
                      <img
                        src={sub.imgSrc.file}
                        alt={`Subtitle ${idx + 1}`}
                        className="w-100"
                        style={{
                          display: "block",
                          marginTop: 16,
                          maxHeight: "500px",
                          objectFit: "contain"
                        }}
                      />
                    </Col>
                  )}
                </Row>
              </Container>
            );
          })}
        </Container>
      </div>

      <div className="d-block d-md-none">
        <h2 className='text-center mb-4'>Introteknik AB</h2>
        <MobileCards content={carouselItems} />
        <Container className="text-center">
          {subtitles.map((sub, idx) => (
            <Container key={idx} className='my-5'>
              <SafeHtml html={l(sub.content.contentSwe, sub.content.contentEng)} />
              {sub.imgSrc.file && (
                <img
                  src={sub.imgSrc.file}
                  alt={`Subtitle ${idx + 1}`}
                  style={{ width: '100%', marginTop: '16px' }}
                />
              )}
            </Container>
          ))}
        </Container>
      </div>

      {/* Mixed Products + References Grid (randomized).
          Desktop (lg+): 2 columns. md/sm: 1 column.
          Pagination: 4 rows per batch => 8 items at a time on lg. */}
      <Container className="my-5">
        <Row xs={1} md={1} lg={2} className="g-4">
          {mixedItems.slice(0, visibleCount).map((item, idx) => {
            const key =
              (item?._id?.$oid ? `${item._kind}-${item._id.$oid}` : `${item._kind}-${idx}`);

            if (item._kind === 'product') {
              const img = item?.imageSrc?.file || null;
              return (
                <Col key={key}>
                  <div className="p-3 border rounded-3 h-100 d-flex flex-column">
                    {img && (
                      <img
                        src={img}
                        alt={item?.name || 'Product image'}
                        className="w-100 mb-3"
                        style={{ maxHeight: 240, objectFit: 'contain' }}
                      />
                    )}

                    <h5 className="mb-2">{item?.name}</h5>
                    <div className="text-muted small mb-2">
                      {[
                        l(item?.category?.categorySwe, item?.category?.categoryEng),
                        l(item?.type?.typeSwe, item?.type?.typeEng),
                      ].filter(Boolean).join(' · ')}
                    </div>
                    <p className="mb-3">
                      {l(truncate100(item?.contentSwe), truncate100(item?.contentEng))}
                    </p>
                    <div className="mt-auto">
                      <a onClick={() => handleReadMore(item)} style={{ cursor: 'pointer', color: '#0d6efd', textDecoration: 'underline' }}>
                        {l('Läs mer', 'Read more')}
                      </a>
                    </div>
                  </div>
                </Col>
              );
            }

            // reference card
            const refImg = pickFirstImageFromRef(item);
            return (
              <Col key={key}>
                <div className="p-3 border rounded-3 h-100 d-flex flex-column">
                  {refImg && (
                    <img
                      src={refImg}
                      alt={l(item?.titleSwe, item?.titleEng) || 'Reference image'}
                      className="w-100 mb-3"
                      style={{ maxHeight: 240, objectFit: 'cover' }}
                    />
                  )}

                  <h5 className="mb-1">{l(item?.titleSwe, item?.titleEng)}</h5>
                  <div className="text-muted small mb-2">
                    {item?.company}
                    {item?.type ? ` · ${item.type}` : ''}
                  </div>

                  <p className="mb-3">
                    {l(truncate100(item?.contentSwe), truncate100(item?.contentEng))}
                  </p>

                  <div className="mt-auto">
                    <a onClick={() => handleReadMore(item)} style={{ cursor: 'pointer', color: '#0d6efd', textDecoration: 'underline' }}>
                      {l('Läs mer', 'Read more')}
                    </a>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>

        {canShowMore && (
          <div className="text-center mt-4">
            <Button variant="primary" onClick={() => setVisibleCount(v => v + BATCH)}>
              {l('Visa mer', 'Show more')}
            </Button>
          </div>
        )}
      </Container>
    </>
  );
};

export default Home;
